import { type SxProps, type Theme, Typography } from "@mui/material";
import type { ElementType, FC, ReactNode } from "react";
import { FONT_SIZE, INK, LETTER_SPACING } from "@/theme/tokens";

type Props = {
	/** Already translated. A few words naming what follows ("How many", "Called in at"). */
	children: ReactNode;
	/**
	 * The element it renders as, for the document outline: `h2`/`h3` when it heads a section, `dt` for a term in a
	 * description list, `span` inside phrasing content.
	 * Absent, a plain `p` — a label over a control, which is what most of these are.
	 */
	component?: ElementType;
	/**
	 * Placement and surface only — margins, padding, an alignment, a band's background (`SheetStepSectionLabel` sets
	 * the label in one). Never its type — size, weight, tracking, case, ink: those are the point of the component.
	 */
	sx?: SxProps<Theme>;
	/** For a section or group that is `aria-labelledby` the label. */
	id?: string;
	/** Kept out of the accessibility tree: the label repeats what its group's own `aria-label` already says. */
	ariaHidden?: boolean;
	/**
	 * The id of the input it names: it renders as that input's `<label>` (whatever `component` says), so the field takes
	 * its accessible name from it and a click on it focuses the field — a label over a text field that has none of its own.
	 */
	htmlFor?: string;
	/**
	 * Semibold (600) instead of bold: a title inside a menu panel (a period menu's "Period", a Summary's "Group by"),
	 * where bold over 14px rows reads louder than the rows it heads. Off, it is the bold label every caller had.
	 */
	semibold?: boolean;
	dataTestId?: string;
};

/**
 * The house uppercase eyebrow: a small, tracked, muted label over a control, a figure or a run of rows — "How many",
 * "Reason", "Called in at", a fact's term on a card.
 *
 * One component because the same five properties (`LABEL_XS`, bold, `LETTER_SPACING.LABEL`, uppercase, `INK.MUTED`)
 * were being restated beside every figure and field on the phone's sheets, and a copy that drifts by one property
 * makes two labels on one card read as two kinds of thing. `SheetStepSectionLabel` is this label set in a band.
 */
export const EyebrowLabel: FC<Props> = ({
	children,
	component,
	sx,
	id,
	ariaHidden,
	htmlFor,
	// DEFAULT: bold, the label every caller had.
	semibold = false,
	dataTestId,
}) => (
	<Typography
		// DEFAULT: Typography's own element for its body variant — the plain-label case, not a missing value.
		component={htmlFor === undefined ? (component ?? "p") : "label"}
		htmlFor={htmlFor}
		id={id}
		aria-hidden={ariaHidden}
		data-testid={dataTestId}
		sx={[
			{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: semibold ? 600 : 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: INK.MUTED },
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{children}
	</Typography>
);
